import { useRef } from 'react';
import type { MouseEvent } from 'react';

/**
 * Backdrop-close handlers for modal overlays.
 *
 * A naive `onClick={onClose}` on the backdrop closes the modal whenever the
 * browser's `click` fires on the overlay — which includes the case where the
 * pointer is PRESSED inside the modal card and RELEASED on the backdrop (the
 * `click` fires on their common ancestor, the overlay). That happens routinely
 * while drag-selecting text to copy/paste, so the modal appears to close on
 * Ctrl+C / Ctrl+V.
 *
 * These handlers close only when the pointer both pressed *and* released on the
 * overlay element itself — i.e. a genuine backdrop click.
 *
 * Usage:
 *   const overlayClose = useOverlayClose(onClose);
 *   <div style={overlay} {...overlayClose}> … </div>
 */
export function useOverlayClose(onClose: () => void) {
  const pressedOnOverlay = useRef(false);

  return {
    onMouseDown: (e: MouseEvent) => {
      pressedOnOverlay.current = e.target === e.currentTarget;
    },
    onClick: (e: MouseEvent) => {
      if (pressedOnOverlay.current && e.target === e.currentTarget) onClose();
    },
  };
}
